import logoEnUrl from '../assets/jubilee-logo-en.png'
import logoUrl from '../assets/jubilee-logo.png'
import markUrl from '../assets/jubilee-mark.png'
import { CHURCH, EM } from '../config/church'
import { SLIDE_FADE_MS, SLIDE_INTERVAL_MS } from '../config/kiosk'

/**
 * What the slideshow shows when there is no poster: a page for the Korean
 * congregation and one for the English Ministry, taking turns with the posters'
 * fade. Both are in the look of jubilee-km.org — cream and latte, curved bands,
 * the deep red of the cross mark, and the faint mark in the background. The KM
 * page's words come from jubilee-km.org, the EM page's from jubileechurch.net.
 *
 * Plain DOM with fixed text; the pictures are bundled with the app, so it shows
 * the same offline.
 */

type Row = { name: string; time: string }

type Page = {
  lang: 'ko' | 'en'
  /** The English page uses the logo without 기쁨의교회 (jubilee-logo-en.png, cut from the full one). */
  logo: 'full' | 'en'
  name: string
  subtitle: string
  /** The verse (KM) or the tagline (EM), one entry per line. */
  quote: string[]
  cite: string
  sections: { heading: string; rows: readonly Row[]; single?: boolean }[]
  address: string
  contact: string
}

const KM: Page = {
  lang: 'ko',
  logo: 'full',
  name: CHURCH.nameKo,
  subtitle: `${CHURCH.nameEn}, Philadelphia`,
  // jubilee-km.org's home page: the church's name verse (희년).
  quote: [
    '너희는 오십년째 해를 거룩하게 하여',
    '그 땅에 있는 모든 주민을 위하여 자유를 공포하라',
    '이 해는 너희에게 희년이니 너희는 각각 자기의 소유지로 돌아가며',
    '각각 자기의 가족에게로 돌아갈지며',
  ],
  cite: '레위기 25:10',
  sections: [
    {
      heading: '주일 예배',
      rows: [
        { name: '1부 예배', time: '8:00 AM' },
        { name: '2부 예배', time: '9:30 AM' },
        { name: '3부 예배', time: '11:30 AM' },
        { name: 'EM 예배', time: '9:30 AM' },
      ],
    },
    {
      heading: '주중 집회',
      single: true,
      rows: [
        { name: '새벽 기도회', time: '화–금 6:00 AM' },
        { name: '금요 집회', time: '금 7:30 PM' },
      ],
    },
  ],
  address: CHURCH.address,
  contact: `${CHURCH.site} · ${CHURCH.tel}`,
}

const EM_PAGE: Page = {
  lang: 'en',
  logo: 'en',
  name: EM.name,
  subtitle: EM.subtitle,
  quote: [EM.tagline],
  cite: EM.taglineRef,
  sections: [{ heading: "Lord's Day Schedule", rows: EM.schedule, single: true }],
  address: CHURCH.address,
  contact: `${EM.site} · ${CHURCH.tel}`,
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function times(rows: readonly Row[], single = false): HTMLElement {
  const list = el('dl', single ? 'welcome__times welcome__times--single' : 'welcome__times')
  for (const row of rows) {
    const item = el('div', 'welcome__time')
    item.append(el('dt', 'welcome__time-name', row.name), el('dd', 'welcome__time-value', row.time))
    list.appendChild(item)
  }
  return list
}

function page(content: Page): HTMLElement {
  const root = el('section', `welcome__page welcome__page--${content.lang}`)
  root.lang = content.lang

  const head = el('header', 'welcome__head')
  const logo = el('img', content.logo === 'en' ? 'welcome__logo welcome__logo--en' : 'welcome__logo')
  logo.src = content.logo === 'en' ? logoEnUrl : logoUrl
  logo.alt = content.name
  head.append(logo, el('hr', 'welcome__rule'), el('p', 'welcome__name', content.name), el('p', 'welcome__name-en', content.subtitle))

  const verse = el('blockquote', 'welcome__verse')
  verse.appendChild(el('span', 'welcome__quote', '“'))
  for (const line of content.quote) verse.appendChild(el('p', 'welcome__verse-line', line))
  verse.appendChild(el('cite', 'welcome__verse-ref', content.cite))

  const worship = el('div', 'welcome__worship')
  content.sections.forEach((section, i) => {
    worship.append(
      el('h2', i === 0 ? 'welcome__heading' : 'welcome__heading welcome__heading--small', section.heading),
      times(section.rows, section.single),
    )
  })

  const foot = el('footer', 'welcome__foot')
  foot.append(el('p', 'welcome__address', content.address), el('p', 'welcome__site', content.contact))

  root.append(head, verse, worship, foot)
  return root
}

/**
 * The no-poster screen. `start` when it is shown and `stop` when it is hidden,
 * so its page timer runs only while it can be seen.
 */
export class Welcome {
  readonly el: HTMLElement
  private readonly pages: HTMLElement[]
  private shown = 0
  private timer: number | undefined

  constructor() {
    this.el = el('div', 'welcome')
    // Background shared by both pages: the curved latte bands, and the cross mark at 5%.
    this.el.append(el('div', 'welcome__band welcome__band--back'), el('div', 'welcome__band welcome__band--front'))
    const mark = el('img', 'welcome__mark')
    mark.src = markUrl
    mark.alt = ''
    this.el.appendChild(mark)

    this.pages = [page(KM), page(EM_PAGE)]
    for (const p of this.pages) {
      p.style.transition = `opacity ${SLIDE_FADE_MS}ms ease`
      this.el.appendChild(p)
    }
    this.show(0)
  }

  /** Starts on the KM page and takes turns from there. Safe to call when already running. */
  start(): void {
    this.stop()
    this.show(0)
    this.schedule()
  }

  stop(): void {
    if (this.timer !== undefined) clearTimeout(this.timer)
    this.timer = undefined
  }

  private schedule(): void {
    this.timer = window.setTimeout(() => {
      this.show((this.shown + 1) % this.pages.length)
      this.schedule()
    }, SLIDE_INTERVAL_MS + SLIDE_FADE_MS)
  }

  private show(index: number): void {
    this.shown = index
    this.pages.forEach((p, i) => p.classList.toggle('is-shown', i === index))
  }
}
